import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../../common/u1/cn";
import { CloseIcon } from "../../../../assets/ui/icons";
import { NotificationItem } from "./NotificationItem";

/* The notifications drawer — the design's `.nscrim` / `.npanel`. */
export const NotificationPanel = ({
  open = false,
  notifications = [],
  onClose = () => {},
  onSelect = () => {},
  title = "Notifications",
  emptyMessage = "No notifications yet.",
  viewAllTo = "/dashboard/request",
  viewAllLabel = "View all requests",
  className = "",
}) => {
  const panelRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    /* Remember what had focus so it can be handed back on close — otherwise
       closing the drawer drops the caret at the top of the document. */
    openerRef.current = document.activeElement;
    panelRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    /* The page behind must not scroll while the drawer is over it. */
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      const opener = openerRef.current;
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const unread = notifications.filter((n) => !n?.is_read).length;

  return (
    <div
      /* A drawer off the right edge above the phone breakpoint; a bottom sheet
         below it, where a 420px panel would leave no page visible and the last
         rows would sit past the fold. */
      className={cn(
        "u1-root fixed inset-0 z-[80] flex justify-end bg-[var(--scrim)]",
        "max-[743px]:items-end max-[743px]:justify-stretch",
        /* Stops above the phone tab bar rather than behind it. */
        "max-[743px]:pb-[var(--tabbar-clear)]"
      )}
      onClick={onClose}
    >
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        /* The scrim closes on click; the panel is inside it, so its own clicks
           must not travel up. */
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "flex h-full w-[420px] max-w-[92vw] flex-col overflow-hidden bg-white outline-none",
          "shadow-[shadow:var(--drawer-shadow)]",
          "max-[743px]:h-auto max-[743px]:max-h-[84dvh] max-[743px]:w-full max-[743px]:max-w-none",
          "max-[743px]:rounded-t-[18px] max-[743px]:shadow-[shadow:var(--sheet-shadow)]",
          className
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--line)] px-6 py-5">
          <div className="min-w-0">
            <h3 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">{title}</h3>
            {notifications.length > 0 && (
              <p className="mt-0.5 mb-0 text-[12.5px] text-[var(--muted)] font-sans">
                {unread > 0 ? `${unread} unread` : "All caught up"}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notifications"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--line)] bg-white text-[var(--ink-2)] transition-colors hover:border-[var(--coral)] hover:text-[var(--coral)]"
          >
            <CloseIcon size={16} />
          </button>
        </header>

        {notifications.length === 0 ? (
          /* `grow basis-0`, not `flex-1`: this project replaces Tailwind's
             `flex` scale in tailwind.config.js, so `flex-1` emits nothing at
             all. See FINDINGS F-015. */
          <p className="grow basis-0 px-6 py-10 text-center text-[14px] text-[var(--muted)] font-sans">
            {emptyMessage}
          </p>
        ) : (
          /* `min-h-0` lets it actually scroll rather than growing past the
             panel; `grow basis-0` stands in for the missing `flex-1`. */
          <ul /* `basis-auto` on the sheet: `basis-0` grows from nothing against the
                 panel's free space, and a bottom sheet has no free space to give —
                 the list collapsed to zero height. */
            className="m-0 min-h-0 grow basis-0 list-none space-y-2.5 overflow-y-auto p-4 max-[743px]:max-h-[52dvh] max-[743px]:basis-auto">
            {notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onSelect={onSelect} />
            ))}
          </ul>
        )}

        {/* Where the bell used to go. Keeping it reachable means opening the
            drawer takes nothing away. */}
        {viewAllTo && (
          <div className="shrink-0 border-t border-[var(--line)] bg-[var(--slate-2)] p-3">
            <Link
              to={viewAllTo}
              onClick={onClose}
              className="flex h-10 items-center justify-center rounded-lg text-[13.5px] font-semibold text-[var(--coral)] transition-colors hover:bg-white font-sans"
            >
              {viewAllLabel}
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
};

export default NotificationPanel;
